import EditableTable from '@/components/Table/EditableTable'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import {
  APPROVAL_TABLE_TYPE,
  buildApproverTableColumn,
  getInvitationButtonText,
} from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import useUser from '@/features/users/useUser'
import { Select } from '@chakra-ui/react'
import { useDispatch } from 'react-redux'
import { ApprovalStatus, BookingApproverRole, PRApprovalType } from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { authSelector } from '@/stores/auth/authSlice'

const PRApproverTable = ({ costBudgetIndex, submissionIndex, vendorSelectionIndex, canEditPR }) => {
  const offerId = useWatch({ name: '_id' })
  const { user } = useSelector(authSelector)
  const approverDetails =
    useWatch({
      name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}.approverDetails`,
    }) ?? []

  const expectedDeliveryDate = useWatch({
    name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}.expectedDeliveryDate`,
  })
  const subSystems = useWatch({ name: 'subSystems' }) ?? []
  const selectedSubSystemName = useWatch({
    name: `sourcing.costBudgets.${costBudgetIndex}.detail.subSystem`,
  })

  const columnHelper = createColumnHelper()
  const { useFetchCostCenter } = useCostCenter()
  const { useFetchAllUsers, useUserOptions } = useUser()
  const { setValue, getValues } = useFormContext()
  const { data: userResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
  })

  const { data: PMOptions } = useUserOptions({
    valueKey: '_id',
    status: 'active',
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, 'sales', 'engineer'],
  })

  const { data: commercialOptions } = useUserOptions({
    valueKey: '_id',
    status: 'active',
    contributorGroup: [ContributorGroupType.COMMERCIAL_PROJECT_MANAGER],
  })

  const { data: procurementOfficerOptions } = useUserOptions({
    status: 'active',
    role: 'user',
    contributorGroup: ContributorGroupType.PROCUREMENT_OFFICER,
    valueKey: '_id',
  })

  const { data: logisticOptions } = useUserOptions({
    status: 'active',
    role: 'user',
    contributorGroup: ContributorGroupType.LOGISTIC,
    valueKey: '_id',
  })

  const selectedCostCenterId = useMemo(() => {
    if (!Array.isArray(subSystems) || !selectedSubSystemName) {
      return ''
    }

    const selectedSubSystem = subSystems.find((subSystem) => subSystem?.projectName === selectedSubSystemName)

    if (!selectedSubSystem?.profitCenter) {
      return ''
    }

    return selectedSubSystem.profitCenter?._id || selectedSubSystem.profitCenter || ''
  }, [subSystems, selectedSubSystemName])

  const { data: selectedCostCenter } = useFetchCostCenter(selectedCostCenterId || '')

  const technical3ApproverOptions = useMemo(() => {
    if (!Array.isArray(selectedCostCenter?.approvers)) {
      return []
    }

    return selectedCostCenter.approvers
      .filter(
        (approverDetail) =>
          approverDetail?.type === BookingApproverRole.TECHNICAL &&
          approverDetail?.level === 3 &&
          approverDetail?.user?._id
      )
      .map((approverDetail) => ({
        value: approverDetail.user._id,
        label: approverDetail.user.fullName || '',
      }))
  }, [selectedCostCenter?.approvers])

  const commercial3ApproverOptions = useMemo(() => {
    if (!Array.isArray(selectedCostCenter?.approvers)) {
      return []
    }

    return selectedCostCenter.approvers
      .filter(
        (approverDetail) =>
          approverDetail?.type === BookingApproverRole.COMMERCIAL &&
          approverDetail?.level === 3 &&
          approverDetail?.user?._id
      )
      .map((approverDetail) => ({
        value: approverDetail.user._id,
        label: approverDetail.user.fullName || '',
      }))
  }, [selectedCostCenter?.approvers])

  const tableData = approverDetails.map((detail) => {
    let userOptions = []
    switch (detail.type) {
      case PRApprovalType.TECHNICAL_PROJECT_MANAGER:
        userOptions = PMOptions
        break
      case PRApprovalType.TECHNICAL_3:
        userOptions = technical3ApproverOptions

        break
      case PRApprovalType.HSP_2:
      case PRApprovalType.HSP_1:
        userOptions = procurementOfficerOptions
        break
      case PRApprovalType.COMMERCIAL_3:
        userOptions = commercial3ApproverOptions

        break
      case PRApprovalType.COMMERCIAL_PROJECT_MANAGER:
        userOptions = commercialOptions
        break
      case PRApprovalType.LOGISTIC:
        userOptions = logisticOptions
        break
    }
    const approverUserId = detail?.user?._id || detail?.user

    // Keep the assigned approver selectable + labeled even if not part of the
    // computed role-based options (e.g. they changed contributor group since
    // assignment), mirroring the fix applied to BookingTabApprovalSection.
    if (approverUserId && !userOptions.some((option) => option.value === approverUserId)) {
      const assignedUserFromList = Array.isArray(userResponse?.data)
        ? userResponse.data.find((candidate) => candidate._id === approverUserId)
        : undefined
      userOptions = [
        ...userOptions,
        {
          value: approverUserId,
          label: detail?.user?.fullName || assignedUserFromList?.fullName || approverUserId,
        },
      ]
    }

    const invitationButtonText = getInvitationButtonText(detail.invitedUsers)

    return {
      ...detail,
      isEffective: false,
      delegationRemark: '',
      userOptions: userOptions,
      disableEditType: true,
      disableEditUser: false,
      disableEditDueDate: detail?.status !== ApprovalStatus.UPCOMING,
      disableEditRemark: true,
      disableRemoveApprover: true,

      invitationButtonText: invitationButtonText,
      canManageInvitations: String(approverUserId || '') === String(user?._id || ''),
    }
  })

  const updateTableData = (rowIndex, columnId, value) => {
    setValue(
      `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}.approverDetails.${rowIndex}.${columnId}`,
      value,
      { shouldDirty: true }
    )
  }

  const dispatch = useDispatch()
  const columnDef = useMemo(
    () =>
      buildApproverTableColumn({
        columnHelper,
        dispatch,
        tableType: APPROVAL_TABLE_TYPE.PR_APPROVAL,

        updateInvitationParameter: {
          offerId,
          costBudgetIndex,
          vendorSelectionIndex,
          submissionIndex,
        },
      }),
    [columnHelper, costBudgetIndex, dispatch, offerId, submissionIndex, user, vendorSelectionIndex]
  )

  useEffect(() => {
    if (expectedDeliveryDate) {
      const currentPRApproverDetails = getValues(
        `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}.approverDetails`
      )

      const currentDate = moment()

      const dateDiff = moment(expectedDeliveryDate).diff(currentDate, 'day')
      const dayBetweenLevel = dateDiff / currentPRApproverDetails.length

      currentPRApproverDetails.forEach((detail, index) => {
        let newDueDate = moment(currentDate).add(Math.floor(dayBetweenLevel * (index + 1)), 'day')
        if (newDueDate.day() === 6) {
          //Sat
          newDueDate = newDueDate.add(2, 'day')
        } else if (newDueDate.day() === 0) {
          newDueDate = newDueDate.add(1, 'day')
        }

        if (newDueDate)
          setValue(
            `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}.approverDetails.${index}.dueDate`,
            newDueDate.toDate()
          )
      })
    }
  }, [expectedDeliveryDate])
  return (
    <EditableTable
      columnDef={columnDef}
      data={tableData}
      updateData={updateTableData}
      disabled={!canEditPR}
      tableWidth="100%"
    />
  )
}

export default PRApproverTable
